import { useSelector } from "react-redux";

import { usersEndpoints } from "../api";
import type { RootState } from "../redux/reducers/session/state";
import useFetch from "./useFetch";

// Local types for tax profile migration status hook
interface TaxProfileMigrationStatusOptions {
  auto?: boolean;
}

/**
 * Hook to fetch tax profile migration status for the current user
 * Returns migration status including grace period information and suggested data
 *
 * Automatically refetches when the user's taxProfile changes in Redux state.
 * Does not fetch for admin users as they don't have tax profiles.
 */
const useTaxProfileMigrationStatus = (options: TaxProfileMigrationStatusOptions = {}) => {
  const { auto = true } = options;

  // Watch for taxProfile changes in Redux - when profile is completed, refetch status
  const taxProfileComplete = useSelector(
    (state: RootState) => state.sessionState?.user?.taxProfile?.taxProfileComplete
  );

  // Check if user is admin - admins don't have tax profiles
  const isAdmin = useSelector((state: RootState) => state.sessionState?.user?.role?.name === "Admin");

  return useFetch(
    () => {
      // Skip the API call for admin users
      if (isAdmin) {
        return Promise.resolve(null);
      }
      return usersEndpoints
        .getTaxProfileMigrationStatus()
        .then(({ data }) => data)
        .catch((err) => {
          console.warn("Failed to fetch tax profile migration status:", err);
          return null;
        });
    },
    {
      auto,
      deps: [taxProfileComplete, isAdmin], // Refetch when taxProfile completion status or admin status changes
      // TODO: Fix type assertion - useFetch cacheKey expects string | undefined
      cacheKey: isAdmin ? undefined : "tax_profile_migration_status", // Don't cache for admins
    }
  );
};

export default useTaxProfileMigrationStatus;
